import { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

export interface BusinessListItem {
  id: string; name: string; owner: string; category: string; city: string;
  phone: string; email: string; queues: number; status: string; revenue: number; joined: string;
}

export interface BusinessFilters { categories: string[]; cities: string[]; statuses: string[] }

interface ListState {
  items: BusinessListItem[];
  filters: BusinessFilters;
  loading: boolean;
  error: string | null;
}

/**
 * Fetches all businesses (real data) plus filter options. With the current
 * dataset size the page filters/paginates client-side over `items`, exactly
 * like before — only the source is now the API.
 */
export function useBusinesses() {
  const [state, setState] = useState<ListState>({
    items: [], filters: { categories: [], cities: [], statuses: [] }, loading: true, error: null,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.BUSINESSES, { params: { page: 1, page_size: 100 } });
      const data = res.data?.data ?? res.data;
      setState({ items: data.items, filters: data.filters, loading: false, error: null });
    } catch (err: any) {
      setState((s) => ({ ...s, loading: false, error: err?.response?.data?.message || 'Failed to load businesses.' }));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { ...state, reload: load };
}

/** The business's free-trial subscription (present even once expired). */
export interface BusinessTrial {
  provider_count: number;
  token_count: number;
  start_date: string;
  end_date: string;
  status: string;
  is_active: boolean;
  days_remaining: number;
}

export interface BusinessDetail {
  business: Record<string, any>;
  stats: Record<string, number>;
  subscription: Record<string, any> | null;
  trial: BusinessTrial | null;
  queues: Array<Record<string, any>>;
  team: Array<Record<string, any>>;
  reviews: { average: number; count: number; items: Array<Record<string, any>> };
}

export interface TrialPatch {
  trial_days?: number;
  provider_count?: number;
  token_count?: number;
}

/**
 * Edits one business's trial (length from its start date, provider/token
 * allowance). Resolves to an error message, or null on success.
 */
export async function updateBusinessTrial(id: string, patch: TrialPatch): Promise<string | null> {
  try {
    await axiosInstance.patch(`${API_ENDPOINTS.BUSINESSES}/${id}/trial`, patch);
    return null;
  } catch (err: any) {
    return err?.response?.data?.message || 'Failed to update trial.';
  }
}

/** Fetches the composite detail for one business. */
export function useBusinessDetail(id: string | undefined) {
  const [data, setData] = useState<BusinessDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`${API_ENDPOINTS.BUSINESSES}/${id}`);
      setData((res.data?.data ?? res.data) as BusinessDetail);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load business.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);
  return { data, loading, error, reload: load };
}
